export type Vec3 = { x: number; y: number; z: number };

export const degToRad = (deg: number): number => (deg * Math.PI) / 180;

export const radToDeg = (rad: number): number => (rad * 180) / Math.PI;

export const normalizedDegrees = (degrees: number): number => ((degrees % 360) + 360) % 360;

/**
 * Apply room heading: clockwise device rotation about room Z.
 * +90 takes top-view 12 o'clock (back, +room Y) to 3 o'clock (+room X).
 * Matches MXA `rotation.xAxis` / SVG clockwise rotate.
 */
export const applyRoomHeading = (room: Vec3, headingDeg: number): Vec3 => {
	const theta = degToRad(headingDeg);
	const cos = Math.cos(theta);
	const sin = Math.sin(theta);
	return {
		x: room.x * cos + room.y * sin,
		y: -room.x * sin + room.y * cos,
		z: room.z
	};
};

export const undoRoomHeading = (room: Vec3, headingDeg: number): Vec3 => applyRoomHeading(room, -headingDeg);

/** `M_ceiling` / inverse (180° about room Y). */
export const applyCeilingIdentity = (v: Vec3): Vec3 => ({ x: -v.x, y: v.y, z: -v.z });

export const deviceLocalToRoom = (
	deviceLocal: Vec3,
	tiltDeg: number,
	wallTurnDeg: number,
	rollDeg: number,
	headingDeg = 0
): Vec3 => {
	// Angles match header: Rx(−tilt) · Ry(−wallTurn) · Rz(+roll).
	const tiltRad = degToRad(-tiltDeg);
	const wallTurnRad = degToRad(-wallTurnDeg);
	const rollRad = degToRad(rollDeg);
	const sinTilt = Math.sin(tiltRad);
	const cosTilt = Math.cos(tiltRad);
	const sinWallTurn = Math.sin(wallTurnRad);
	const cosWallTurn = Math.cos(wallTurnRad);
	const sinRoll = Math.sin(rollRad);
	const cosRoll = Math.cos(rollRad);

	// Rz(+roll)
	const xAfterRoll = deviceLocal.x * cosRoll - deviceLocal.y * sinRoll;
	const yAfterRoll = deviceLocal.x * sinRoll + deviceLocal.y * cosRoll;
	const zAfterRoll = deviceLocal.z;

	// Ry(−wallTurn)
	const xAfterWallTurn = xAfterRoll * cosWallTurn + zAfterRoll * sinWallTurn;
	const yAfterWallTurn = yAfterRoll;
	const zAfterWallTurn = -xAfterRoll * sinWallTurn + zAfterRoll * cosWallTurn;

	// Rx(−tilt): stored +90 tilts the grille toward +room Y (back / 12 o'clock).
	const xAfterTilt = xAfterWallTurn;
	const yAfterTilt = yAfterWallTurn * cosTilt - zAfterWallTurn * sinTilt;
	const zAfterTilt = yAfterWallTurn * sinTilt + zAfterWallTurn * cosTilt;

	return applyRoomHeading({ x: -xAfterTilt, y: yAfterTilt, z: -zAfterTilt }, headingDeg);
};

export const roomToDeviceLocal = (
	room: Vec3,
	tiltDeg: number,
	wallTurnDeg: number,
	rollDeg: number,
	headingDeg = 0
): Vec3 => {
	const unheaded = undoRoomHeading(room, headingDeg);
	// Inverse: Rz(−roll) · Ry(+wallTurn) · Rx(+tilt) with the same plate angles as forward.
	const tiltRad = degToRad(-tiltDeg);
	const wallTurnRad = degToRad(-wallTurnDeg);
	const rollRad = degToRad(rollDeg);
	const sinTilt = Math.sin(tiltRad);
	const cosTilt = Math.cos(tiltRad);
	const sinWallTurn = Math.sin(wallTurnRad);
	const cosWallTurn = Math.cos(wallTurnRad);
	const sinRoll = Math.sin(rollRad);
	const cosRoll = Math.cos(rollRad);

	const xCeil = -unheaded.x;
	const yCeil = unheaded.y;
	const zCeil = -unheaded.z;

	// Inverse Rx(−tilt) = Rx(+tilt)
	const xAfterTilt = xCeil;
	const yAfterTilt = yCeil * cosTilt + zCeil * sinTilt;
	const zAfterTilt = -yCeil * sinTilt + zCeil * cosTilt;

	// Inverse Ry(−wallTurn) = Ry(+wallTurn)
	const xAfterWallTurn = xAfterTilt * cosWallTurn - zAfterTilt * sinWallTurn;
	const yAfterWallTurn = yAfterTilt;
	const zAfterWallTurn = xAfterTilt * sinWallTurn + zAfterTilt * cosWallTurn;

	// Inverse Rz(+roll) = Rz(−roll)
	return {
		x: xAfterWallTurn * cosRoll + yAfterWallTurn * sinRoll,
		y: -xAfterWallTurn * sinRoll + yAfterWallTurn * cosRoll,
		z: zAfterWallTurn
	};
};

/** Room-frame images of device-local +X / +Y / +Z. */
export type DeviceLocalAxesInRoom = { x: Vec3; y: Vec3; z: Vec3 };

export const deviceLocalAxesInRoom = (
	tiltDeg: number,
	wallTurnDeg: number,
	rollDeg: number,
	headingDeg = 0
): DeviceLocalAxesInRoom => ({
	x: deviceLocalToRoom({ x: 1, y: 0, z: 0 }, tiltDeg, wallTurnDeg, rollDeg, headingDeg),
	y: deviceLocalToRoom({ x: 0, y: 1, z: 0 }, tiltDeg, wallTurnDeg, rollDeg, headingDeg),
	z: deviceLocalToRoom({ x: 0, y: 0, z: 1 }, tiltDeg, wallTurnDeg, rollDeg, headingDeg)
});

/**
 * Extract device-local Euler from room-frame axes for composition
 * `M_ceiling · Rx(−tilt) · Ry(−wallTurn) · Rz(roll)` (no outer heading).
 *
 * Q = M · R = Rx(ax) · Ry(ay) · Rz(az) with ax = −tilt, ay = −wallTurn, az = roll.
 * Gimbal lock when cos(wallTurn) ≈ 0: set roll = 0 and fold into tilt.
 */
export const extractDeviceLocalEulerFromAxes = (
	axes: DeviceLocalAxesInRoom
): { xAxis: number; yAxis: number; zAxis: number } => {
	const qx = applyCeilingIdentity(axes.x);
	const qy = applyCeilingIdentity(axes.y);
	const qz = applyCeilingIdentity(axes.z);

	const r00 = qx.x;
	const r01 = qy.x;
	const r02 = qz.x;
	const r10 = qx.y;
	const r11 = qy.y;
	const r12 = qz.y;
	const r22 = qz.z;

	const sinY = r02;
	const cosY = Math.hypot(r00, r01);
	const gimbalLock = cosY < 1e-8;

	if (gimbalLock) {
		// Straight up/down tilt: fold roll into tilt.
		const ay = sinY >= 0 ? Math.PI / 2 : -Math.PI / 2;
		const ax = sinY >= 0 ? Math.atan2(r10, r11) : Math.atan2(-r10, r11);
		return {
			xAxis: normalizedDegrees(radToDeg(-ax)),
			yAxis: normalizedDegrees(radToDeg(-ay)),
			zAxis: 0
		};
	}

	const ay = Math.atan2(sinY, cosY);
	const ax = Math.atan2(-r12, r22);
	const az = Math.atan2(-r01, r00);

	return {
		xAxis: normalizedDegrees(radToDeg(-ax)),
		yAxis: normalizedDegrees(radToDeg(-ay)),
		zAxis: normalizedDegrees(radToDeg(az))
	};
};
